import React, { useEffect, useRef, useState } from "react";
import * as echarts from "echarts";
import { EChartsOption } from "echarts";
import { RiverSensor } from "../../riverSensors/RiverSensors";

interface sensorItem {
  id: string;
  name: string;
  critical: number;
  cautious: number;
  warning: number;
  fullScreen: boolean;
  showDialog: boolean;
  chartWidth: number;
  chartHeight: number;
}

const EWSRiverChart: React.FC<sensorItem> = ({
  id,
  name,
  critical,
  cautious,
  warning,
  fullScreen,
  showDialog,
  chartWidth,
  chartHeight
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const [chartInstance, setChartInstance] = useState<echarts.ECharts>();
  const [initialData, setInitialData] = useState<any[]>([]);
  const [initialLoad, setInitialLoad] = useState<boolean>(true);
  const RIVER_LEVEL_GRAPH_URL = process.env.REACT_APP_RIVER_LEVEL_GRAPH_URL;
  
  useEffect(() => {
    const fetchDataAndInitializeChart = async () => {
      try {
        const response = await fetch(
         `${RIVER_LEVEL_GRAPH_URL}` +
            id
        );
        const responseData = await response.json();

        if (responseData && Array.isArray(responseData)) {
          const data = responseData.map((item: any) => [
            item.timestamp,
            item.value,
          ]);
          setInitialData(data);
        } else {
          console.error("Invalid response data format:", responseData);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchDataAndInitializeChart();
  }, [id]);

  useEffect(() => {
    if (
      chartRef.current &&
      chartRef.current.clientWidth &&
      chartRef.current.clientHeight
    ) {
      const chart = echarts.init(chartRef.current);

      const chartOption: EChartsOption = {
        color: ["#80FFA5", "#00DDFF", "#37A2FF", "#FF0087", "#FFBF00"],
        title: {
          left: "center",
          text: name,
          textStyle: {
            color: "#FFFFFF",
            fontSize:"14px",
          },
        },
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "cross",
            label: {
              backgroundColor: "#6a7985",
            },
          },
        },
        legend: {
          data: [name],
          textStyle: {
            color: "#FFFFFF",
          
          },
          right: "10%",
          top: "10%",
        },
        toolbox: {
          feature: {
            dataZoom: {
              yAxisIndex: "none",
            },
            restore: {},
            saveAsImage: {},
          },
        },
        grid: {
          left: "3%",
          right: "14%",
          bottom: "20%",
          containLabel: true,
        },
        xAxis: {
          type: "time",
          name: "Time",
          axisLabel: {
            color: "#FFFFFF",
          },
          // boundaryGap: [0, "100%"],
          axisLine: {
            lineStyle: {
              color: "#FFFFFF",
              width: 2,
            },
          },
        },
        yAxis: {
          type: "value",
          name: "Water Level",
          nameTextStyle: {
            color: "#FFFFFF",
          },
          axisLabel: {
            formatter: "{value} m",
            color: "#FFFFFF",
          },
          boundaryGap: [0, "100%"],
          axisLine: {
            lineStyle: {
              color: "#070807",
              width: 2,
            },
          },
          splitLine: {
            lineStyle: {
              color: "rgba(85, 84, 95, 0.6)",
              width: 1,
            },
          },
          // ...splitLineStyle,
        },

        dataZoom: [
          {
            type: "inside",
            start: 0,
            end: 100,
          },
          {
            start: 0,
            end: 100,
          },
        ],
        visualMap: {
          type: "piecewise",
          show: false,
          dimension: 1,
          pieces: [
            {
              gt: 0,
              lt: cautious,
              color: "rgba(105,125,194,0.9)",
            },
            {
              gt: cautious,
              lt: warning,
              color: "#ffff00",
            },
            {
              gt: warning,
              lt: critical,
              color: "#ff8c00",
            },
            {
              gt: critical,
              color: "#f00e0e",
            },
          ],
        },
        series: [
          {
            name: name,
            type: "line",
            stack: "Total",
            smooth: true,
            symbol: "none",
            lineStyle: {
              width: 0,
            },
            areaStyle: {
              opacity: 0.8,
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                {
                  offset: 0,
                  color: "rgb(0, 221, 255)",
                },
                {
                  offset: 1,
                  color: "rgb(77, 119, 255)",
                },
              ]),
            },
            emphasis: {
              focus: "series",
            },
            data: initialData,
            markLine: {
              symbol: "circle",
              data: [
                {
                  name: "Danger",
                  yAxis: critical,
                  itemStyle: {
                    color: "red",
                  },
                  label: {
                    formatter: " Danger Level",
                  },
                },
                {
                  name: "Warning",
                  yAxis: warning,
                  itemStyle: {
                    color: "orange",
                  },
                  label: {
                    formatter: " Warning Level",
                  },
                },
                {
                  name: "Cautious",
                  yAxis: cautious,
                  itemStyle: {
                    color: "yellow",
                  },
                  label: {
                    formatter: " Cautious Level",
                  },
                },
              ],
            },
          },
        ],
      };

      chart.setOption(chartOption);
      setChartInstance(chart);
    }

    return () => {
      if (chartInstance) {
        chartInstance.dispose();
      }
    };
  }, [initialData]);

  const RIVER_LEVEL_THREE_DAYS_URL = process.env.REACT_APP_RIVER_LEVEL_THREE_DAYS_URL;

  const generateNewData = async () => {
    try {
      const response = await fetch(
        `${RIVER_LEVEL_THREE_DAYS_URL}` +
          id
      );
      const responseData = await response.json();

      if (responseData && Array.isArray(responseData.results)) {
        const newData = responseData.results.map((item: any) => [
          item.timestamp,
          item.value,
        ]);
        setInitialData((prevData) => [...prevData, ...newData]);
      } else {
        console.error("Invalid response data format:", responseData);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    if (showDialog) {
      const interval = setInterval(() => {
        generateNewData();
      }, 15 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [showDialog]);

 

  useEffect(() => {
    if (chartInstance) {
      setTimeout(() => {
        chartInstance.resize();
      }, 100);
    }
  }, [fullScreen]);

  useEffect(() => {
    const handleResize = () => {
      if (chartInstance) {
        chartInstance.resize();
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [chartInstance]);

  useEffect(() => {

    if(chartWidth !== 0){
      setInitialLoad(false);
    }
  }, [chartWidth,initialLoad]);

  useEffect(() => {
    if (chartInstance) {
      chartInstance.resize();
    }
  }, [chartWidth,chartHeight]); 

  return (
    <div
      id="main"
      style={{ width:initialLoad?"100%" :`${chartWidth}px`, height:initialLoad?"45vh" :`${chartHeight}px `, marginTop:"5px"}} 
      ref={chartRef}
    ></div>
  );
};

export default EWSRiverChart;

function generateRandomGradientColors(
  count: number
): echarts.graphic.LinearGradient[] {
  const colors: echarts.graphic.LinearGradient[] = [];
  for (let i = 0; i < count; i++) {
    const startColor = getRandomColor();
    const endColor = getRandomColor();
    colors.push(
      new echarts.graphic.LinearGradient(0, 0, 0, 1, [
        { offset: 0, color: startColor },
        { offset: 1, color: endColor },
      ])
    );
  }
  return colors;
}

function getRandomColor(): string {
  const letters = "0123456789ABCDEF";
  let color = "#";
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
}
